import {
  ORDER_STATUS_LABELS_LAO,
  type OrderListRow,
} from "@/modules/orders/domain/types";
import { formatDateTimeLocal } from "@/shared/lib/date-time";
import { DataTable, createSortableColumn, type TanstackReactTable } from "@/shared/ui/data-table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";


import { Link } from "@tanstack/react-router";
import { EyeIcon, PhoneIcon, ShoppingBagIcon } from "lucide-react";

const STATUS_BADGE_VARIANTS: Record<
  OrderListRow["status"],
  "default" | "secondary" | "destructive" | "outline"
> = {
  DRAFT: "outline",
  CONFIRMED: "secondary",
  PREPARING: "default",
  READY: "default",
  COMPLETED: "secondary",
  CANCELLED: "destructive",
};

const CHANNEL_LABELS: Record<OrderListRow["channel"], string> = {
  APP: "ແອັບພິເຄຊັນ",
  WALKIN: "ຫນ້າຮ້ານ",
};

const PICKUP_LABELS: Record<OrderListRow["pickupType"], string> = {
  ASAP: "ຮັບທັນທີ",
  SLOT: "ສັ່ງລ່ວງຫນ້າ",
};

type OrdersTableProps = {
  data: OrderListRow[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange: (id: string, desc: boolean) => void;
  onPaginationChange: (offset: number, limit: number) => void;
  onView: (id: string) => void;
};

export function OrdersTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  sortBy,
  sortOrder,
  onSortingChange,
  onPaginationChange,
  onView,
}: OrdersTableProps) {
  const columns: TanstackReactTable.ColumnDef<OrderListRow>[] = [
    {
      accessorKey: "orderNo",
      header: "ລຳດັບ",
      cell: ({ row }) => (
        <Link
          to="/app/orders/$id"
          params={{ id: row.original.id }}
          className="font-medium text-primary hover:underline"
        >
          {row.original.orderNo}
        </Link>
      ),
      size: 140,
      enableSorting: false,
    },
    {
      accessorKey: "branchName",
      header: "ສາຂາ",
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {row.original.branchName}
        </span>
      ),
      size: 120,
      enableSorting: false,
    },
    {
      accessorKey: "channel",
      header: "ຊ່ອງທາງ",
      cell: ({ row }) => (
        <span className="flex items-center gap-2 text-muted-foreground text-sm">
          {CHANNEL_LABELS[row.original.channel] === "APP" ? (
            <PhoneIcon className="size-4" />
          ) : (
            <ShoppingBagIcon className="size-4" />
          )}{" "}
          {CHANNEL_LABELS[row.original.channel]}
        </span>
      ),
      size: 80,
      enableSorting: false,
    },
    {
      accessorKey: "status",
      header: "ສະຖານະ",
      cell: ({ row }) => (
        <Badge variant={STATUS_BADGE_VARIANTS[row.original.status]}>
          {ORDER_STATUS_LABELS_LAO[row.original.status]}
        </Badge>
      ),
      size: 110,
      enableSorting: false,
    },
    createSortableColumn<OrderListRow>("total", "ຍອດ (LAK)", {
      size: 110,
      cell: ({ row }) =>
        Number(row.original.total).toLocaleString("lo-LA", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }),
    }),
    {
      accessorKey: "pickupType",
      header: "ຮັບ",
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {PICKUP_LABELS[row.original.pickupType]}
        </span>
      ),
      size: 70,
      enableSorting: false,
    },
    createSortableColumn<OrderListRow>("pickupAt", "ວັນນັບຮັບ", {
      size: 150,
      cell: ({ row }) =>
        row.original.pickupAt
          ? formatDateTimeLocal(row.original.pickupAt)
          : "–",
    }),
    createSortableColumn<OrderListRow>("createdAt", "ວັນທີສ້າງ", {
      size: 160,
      cell: ({ row }) => formatDateTimeLocal(row.original.createdAt),
    }),
    {
      id: "actions",
      header: "",
      cell: ({ row }) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onView(row.original.id)}
        >
          <EyeIcon className="size-4" />
          ເບິ່ງລາຍລະອຽດ
        </Button>
      ),
      size: 120,
      enableSorting: false,
    },
  ];

  return (
    <DataTable<OrderListRow, unknown>
      noDataMessage="ບໍ່ພົບອໍເດີ"
      isLoading={isLoading}
      columns={columns}
      data={data}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSortingChange={(sorting) => {
        if (sorting[0]?.id === "") return;
        onSortingChange(sorting[0]?.id as string, !!sorting[0]?.desc);
      }}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
    />
  );
}
